import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface CatalogApiRow {
  readonly apiId: string;
  readonly apiCode: string;
  readonly apiName: string;
  readonly apiType: string;
  readonly currentVersion: string;
  readonly status: string;
}
interface ConsumerRow {
  readonly consumerId: string;
  readonly consumerCode: string;
  readonly consumerName: string;
  readonly consumerType: string;
  readonly status: string;
}

const CONSUMER_TYPES = ['INTERNAL', 'PARTNER', 'EXTERNAL', 'MOBILE', 'SYSTEM'];

/** M29 — Developer Portal (§33 / §26-§27). Self-service catalogue of published
 * APIs available for subscription plus the registered API consumers and their
 * lifecycle status. Operates consumer onboarding — register → rotate-key →
 * suspend/reactivate → delete — each wired to the guarded /v1/eip/portal write
 * API. API keys are never displayed — only hashes are stored. */
export function M29DeveloperPortalPage(): ReactNode {
  const { token } = useAuth();
  const { t: tr } = useTranslation();
  const [apis, setApis] = useState<CatalogApiRow[]>([]);
  const [consumers, setConsumers] = useState<ConsumerRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // register form
  const [name, setName] = useState('');
  const [consumerType, setConsumerType] = useState('PARTNER');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: CatalogApiRow[] }>('/v1/eip/portal/catalog', { token }),
      apiRequest<{ data: ConsumerRow[] }>('/v1/eip/portal/consumers', { token }),
    ])
      .then(([c, u]) => { if (alive) { setApis(c.data); setConsumers(u.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m29.devPortal.notAuthorised', { verb, perm })
      : (err as Error).message;

  const register = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!name.trim()) { setActionError(tr('m29.devPortal.validationName')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: ConsumerRow }>(`/v1/eip/portal/consumers`, {
        method: 'POST',
        body: { consumerName: name.trim(), consumerType },
        token,
      });
      setNotice(tr('m29.devPortal.noticeRegistered', { code: r.data.consumerCode }));
      setName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m29.devPortal.verbRegister'), 'M29_API_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const runRowAction = async (
    fn: () => Promise<unknown>,
    successMsg: string,
    verb: string,
    perm: string,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const rotateKey = (c: ConsumerRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/portal/consumers/${c.consumerId}/rotate-key`, { method: 'POST', body: {}, token }),
      tr('m29.devPortal.noticeRotated', { code: c.consumerCode }), tr('m29.devPortal.verbRotate'), 'M29_API_MANAGE',
    );

  const suspend = (c: ConsumerRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/portal/consumers/${c.consumerId}/suspend`, { method: 'POST', body: {}, token }),
      tr('m29.devPortal.noticeSuspended', { code: c.consumerCode }), tr('m29.devPortal.verbSuspend'), 'M29_API_MANAGE',
    );

  const reactivate = (c: ConsumerRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/portal/consumers/${c.consumerId}/reactivate`, { method: 'POST', body: {}, token }),
      tr('m29.devPortal.noticeReactivated', { code: c.consumerCode }), tr('m29.devPortal.verbReactivate'), 'M29_API_MANAGE',
    );

  const remove = (c: ConsumerRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/portal/consumers/${c.consumerId}`, { method: 'DELETE', token }),
      tr('m29.devPortal.noticeDeleted', { code: c.consumerCode }), tr('m29.devPortal.verbDelete'), 'M29_API_MANAGE',
    );

  return (
    <main aria-label="developer-portal">
      <h1>{tr('m29.devPortal.title')}</h1>

      <section aria-label="register-consumer">
        <h2>{tr('m29.devPortal.registerHeading')}</h2>
        <form aria-label="consumer-form" onSubmit={(e) => void register(e)}>
          <label>{tr('m29.devPortal.fieldName')}<input aria-label="field-consumer-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{tr('m29.devPortal.fieldType')}
            <select aria-label="field-consumer-type" value={consumerType} onChange={(e) => setConsumerType(e.target.value)}>
              {CONSUMER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-consumer" disabled={busy}>{busy ? tr('m29.devPortal.registering') : tr('m29.devPortal.register')}</button>
        </form>
        {actionError && <p role="alert" aria-label="consumer-error">{actionError}</p>}
        {notice && <p aria-label="consumer-notice">{notice}</p>}
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m29.devPortal.loading')}</p> : (
        <>
          <section aria-label="published-apis">
            <h2>{tr('m29.devPortal.publishedHeading')}</h2>
            <p aria-label="catalog-count">{tr('m29.devPortal.catalogCount', { count: apis.length })}</p>
            <table aria-label="catalog-table">
              <thead><tr><th>{tr('m29.devPortal.catColCode')}</th><th>{tr('m29.devPortal.catColName')}</th><th>{tr('m29.devPortal.catColType')}</th><th>{tr('m29.devPortal.catColVersion')}</th><th>{tr('m29.devPortal.catColStatus')}</th></tr></thead>
              <tbody>
                {apis.map((a) => (
                  <tr key={a.apiId}>
                    <td>{a.apiCode}</td><td>{a.apiName}</td><td>{a.apiType}</td>
                    <td>{a.currentVersion}</td><td>{a.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <section aria-label="api-consumers">
            <h2>{tr('m29.devPortal.consumersHeading')}</h2>
            <p aria-label="consumer-count">{tr('m29.devPortal.consumerCount', { count: consumers.length })}</p>
            <table aria-label="consumer-table">
              <thead><tr><th>{tr('m29.devPortal.conColCode')}</th><th>{tr('m29.devPortal.conColName')}</th><th>{tr('m29.devPortal.conColType')}</th><th>{tr('m29.devPortal.conColStatus')}</th><th>{tr('m29.devPortal.conColActions')}</th></tr></thead>
              <tbody>
                {consumers.map((c) => (
                  <tr key={c.consumerId}>
                    <td>{c.consumerCode}</td><td>{c.consumerName}</td><td>{c.consumerType}</td><td>{c.status}</td>
                    <td>
                      <button type="button" aria-label={`rotate-key-${c.consumerCode}`} onClick={() => void rotateKey(c)}>{tr('m29.devPortal.btnRotateKey')}</button>
                      <button type="button" aria-label={`suspend-${c.consumerCode}`} onClick={() => void suspend(c)}>{tr('m29.devPortal.btnSuspend')}</button>
                      <button type="button" aria-label={`reactivate-${c.consumerCode}`} onClick={() => void reactivate(c)}>{tr('m29.devPortal.btnReactivate')}</button>
                      <button type="button" aria-label={`delete-${c.consumerCode}`} onClick={() => void remove(c)}>{tr('m29.devPortal.btnDelete')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </main>
  );
}
